import qs from "qs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";

import useGetRequest from "./useGetRequest";
import { SID_LATEST_NOTIFICATIONS_LIMIT } from "../lib/notifications/sidNotificationTypes";
import setupNotificationMessage from "../lib/notifications/setupNotificationMessage";
import { getLatestNotificationFromAllTabMatchingTypes } from "../redux/modules/newNotifications/selector";

export default function useSidLatestNotifications({
  authToken,
  locale,
  userData,
  enabledNotificationTypes = []
}) {
  const store = useStore();
  const latestMatchingNotification = useSelector(state =>
    getLatestNotificationFromAllTabMatchingTypes(state, enabledNotificationTypes)
  );
  const notificationTypesQuery = useMemo(() => {
    if (!enabledNotificationTypes.length) {
      return null;
    }

    return qs.stringify(
      { notification_type: enabledNotificationTypes },
      { arrayFormat: "repeat" }
    );
  }, [enabledNotificationTypes]);

  const { response, loading } = useGetRequest({
    urlPath:
      authToken && notificationTypesQuery ? "app.notification.browser" : null,
    variables: { lastId: `?${notificationTypesQuery}` },
    locale,
    clearResponseOnVariablesChange: true
  });

  const [notifications, setNotifications] = useState([]);
  const initialLoadDoneRef = useRef(false);
  const knownIdsRef = useRef(new Set());

  const mergeLiveNotification = useCallback(
    notification => {
      if (
        !notification ||
        knownIdsRef.current.has(notification.id) ||
        !enabledNotificationTypes.includes(notification.notification_type)
      ) {
        return;
      }

      knownIdsRef.current.add(notification.id);

      setNotifications(previousNotifications => {
        const withoutDuplicate = previousNotifications.filter(
          ({ id }) => id !== notification.id
        );

        return [{ ...notification }, ...withoutDuplicate].slice(
          0,
          SID_LATEST_NOTIFICATIONS_LIMIT
        );
      });
    },
    [enabledNotificationTypes]
  );

  useEffect(() => {
    initialLoadDoneRef.current = false;
    knownIdsRef.current = new Set();
    setNotifications([]);
  }, [notificationTypesQuery]);

  useEffect(() => {
    if (!authToken || !notificationTypesQuery) {
      return;
    }

    if (!response?.messages) {
      return;
    }

    const initialNotifications = response.messages
      .map(message =>
        setupNotificationMessage(
          message,
          response.markets,
          response.tickers,
          userData
        )
      )
      .slice(0, SID_LATEST_NOTIFICATIONS_LIMIT);

    knownIdsRef.current = new Set(initialNotifications.map(({ id }) => id));
    initialLoadDoneRef.current = true;
    setNotifications(initialNotifications);

    // Catch notifications that arrived via socket before this fetch finished.
    mergeLiveNotification(
      getLatestNotificationFromAllTabMatchingTypes(
        store.getState(),
        enabledNotificationTypes
      )
    );
  }, [
    authToken,
    enabledNotificationTypes,
    mergeLiveNotification,
    notificationTypesQuery,
    response,
    store,
    userData
  ]);

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      return;
    }

    mergeLiveNotification(latestMatchingNotification);
  }, [latestMatchingNotification, mergeLiveNotification]);

  return {
    notifications,
    loading: notificationTypesQuery ? loading : false
  };
}
